/** Marks a composed property node as an alias of a canonical host key. */
export const COMPOSE_ALIAS_CANONICAL_KEY = Symbol('composeAliasCanonicalKey');

export function markComposeAliasCanonical(node: unknown, canonicalKey: string): void {
  if (!node || typeof node !== 'object') {
    return;
  }
  Object.defineProperty(node, COMPOSE_ALIAS_CANONICAL_KEY, {
    value: canonicalKey,
    enumerable: false,
    configurable: true,
  });
}

/** Collects alias → canonical maps from top-level composed property nodes. */
export function collectComposeAliasesFromProperties(
  properties: unknown,
): Readonly<Record<string, string>> {
  const aliases: Record<string, string> = {};

  if (!properties || typeof properties !== 'object') {
    return aliases;
  }

  for (const [aliasKey, node] of Object.entries(properties as Record<string, unknown>)) {
    if (!node || typeof node !== 'object' || !(COMPOSE_ALIAS_CANONICAL_KEY in node)) {
      continue;
    }
    const canonical = (node as Record<symbol, string>)[COMPOSE_ALIAS_CANONICAL_KEY];
    if (canonical) {
      aliases[aliasKey] = canonical;
    }
  }

  return aliases;
}

/**
 * Promotes alias keys from a composed block config to their canonical host keys
 * so downstream atoms can consume `resolvedConfig()` without manual mapping.
 */
export function applyComposeAliases<Config extends Record<string, unknown>>(
  resolved: Config,
  aliasesToCanonical: Readonly<Record<string, string>>,
): Config {
  const out: Record<string, unknown> = { ...resolved };

  for (const [aliasKey, canonicalKey] of Object.entries(aliasesToCanonical)) {
    if (out[aliasKey] !== undefined && out[canonicalKey] === undefined) {
      out[canonicalKey] = out[aliasKey];
    }
  }

  return out as Config;
}
